// src/pages/SwordTextGen/index.js
import React, { useState } from "react";
import { 
  Input, Button, Card, message, Typography, Space, Divider, Spin, 
  Collapse, Slider, Row, Col, InputNumber, Layout 
} from "antd";
import { 
  SendOutlined, SaveOutlined, ClearOutlined, SettingOutlined, GithubOutlined, InfoCircleOutlined 
} from "@ant-design/icons";

const { TextArea } = Input;
const { Title, Paragraph, Text } = Typography;
const { Content } = Layout;

const API_BASE_URL = "https://api.xizaijianqishu.top";


export default function SwordTextGen() {
  const [inputText, setInputText] = useState("");
  const [generatedText, setGeneratedText] = useState("");
  const [loading, setLoading] = useState(false);
  
  // 添加参数设置状态
  const [params, setParams] = useState({
    max_new_tokens: 500, // 文本生成长度
    temperature: 1,     // 生成温度
    top_k: 10,            // top-k采样
    top_p: 0.85,          // top-p采样
    repetition_penalty: 1.2, // 重复惩罚
    no_repeat_ngram_size: 0, // 防止重复N-gram
    dynamic_temperature: false, // 动态温度 - 修正为布尔值
    dynamic_top_p: false,      // 动态top-p - 修正为布尔值
    presence_penalty: 0.0,   // 存在惩罚
    frequency_penalty: 0.0,  // 频率惩罚
  });

  const handleGenerate = async () => {
    if (!inputText?.trim()) { // 添加可选链
      message.warning("请输入内容");
      return;
    }

    setLoading(true);
    try {
      // 这里将调用后端接口，包含参数设置
      const response = await fetch(`${API_BASE_URL}/api/sword-text-gen`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          "Referer": "https://xizaijianqishu.top/sword-text-gen",
        },
        body: JSON.stringify({ 
          prompt: inputText,
          ...params // 将所有参数传递给后端
        }),
      });
      
      // 在handleGenerate函数中
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setGeneratedText(data.generated_text || ""); // 使用正确的字段名
          message.success(data.message || "文本生成成功");
        } else {
          message.error(data.message || "文本生成失败");
        }
      } else {
        // 处理HTTP错误
        try {
          const errorData = await response.json();
          message.error(errorData.message || "文本生成失败");
        } catch (e) {
          message.error("文本生成失败");
        }
      }
    } catch (error) {
      console.error("生成文本错误:", error);
      message.error("文本生成失败，请稍后重试");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = () => {
    if (!generatedText?.trim()) { // 添加可选链
      message.warning("没有可保存的文本");
      return;
    }
    // 保存功能可以在后端完成，这里先模拟
    message.info("保存功能待后端实现");
  };

  const handleClear = () => {
    setInputText("");
    setGeneratedText("");
  };

  // 参数变化处理函数
  const handleParamChange = (key, value) => {
    setParams(prev => ({
      ...prev,
      [key]: value
    }));
  };

  return (
    <Layout style={{ background: 'transparent' }}>
      <Content className="main-wrap" style={{ padding: '20px' }}>
        {/* 项目标题与介绍 */}
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <Title level={1} style={{ color: 'var(--theme-primary)', marginBottom: '16px' }}>
            📚 剑来文本生成系统
          </Title>
          <Paragraph style={{ maxWidth: '800px', margin: '0 auto', color: 'var(--theme-text)' }}>
            基于深度学习的《剑来》风格文本生成系统，让你轻松创作具有烽火戏诸侯文风的故事片段
          </Paragraph>
        </div>

        {/* 项目特点 */}
        <Row gutter={[16, 16]} style={{ marginBottom: '32px' }}>
          <Col xs={24} sm={12} lg={8}>
            <Card 
              hoverable
              style={{ background: 'var(--theme-card-bg)', border: '1px solid var(--theme-card-border)' }}
            >
              <Space direction="vertical" style={{ width: '100%' }}>
                <div style={{ fontSize: '24px', color: 'var(--theme-primary)' }}>🎨</div>
                <Title level={4} style={{ margin: '8px 0', color: 'var(--theme-text)' }}>风格精准</Title>
                <Paragraph style={{ margin: 0, fontSize: '14px', color: 'var(--theme-text-secondary)' }}>
                  学习《剑来》原著风格，生成文本保持高度一致性
                </Paragraph>
              </Space>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={8}>
            <Card 
              hoverable
              style={{ background: 'var(--theme-card-bg)', border: '1px solid var(--theme-card-border)' }}
            >
              <Space direction="vertical" style={{ width: '100%' }}>
                <div style={{ fontSize: '24px', color: 'var(--theme-primary)' }}>⚡</div>
                <Title level={4} style={{ margin: '8px 0', color: 'var(--theme-text)' }}>易于复现</Title>
                <Paragraph style={{ margin: 0, fontSize: '14px', color: 'var(--theme-text-secondary)' }}>
                  数据集公开，配置为私人笔记本，人人可复现
                </Paragraph>
              </Space>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={8}>
            <Card 
              hoverable
              style={{ background: 'var(--theme-card-bg)', border: '1px solid var(--theme-card-border)' }}
            >
              <Space direction="vertical" style={{ width: '100%' }}>
                <div style={{ fontSize: '24px', color: 'var(--theme-primary)' }}>🎛️</div>
                <Title level={4} style={{ margin: '8px 0', color: 'var(--theme-text)' }}>灵活配置</Title>
                <Paragraph style={{ margin: 0, fontSize: '14px', color: 'var(--theme-text-secondary)' }}>
                  支持多种生成参数调整，满足不同创作场景需求
                </Paragraph>
              </Space>
            </Card>
          </Col>
        </Row>

      <Card
        className="card-glow"
        style={{ marginBottom: "20px" }}
        title="输入开头"
      >
        <TextArea
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          placeholder="输入生成文本的开头："
          rows={4}
          style={{ marginBottom: "10px", color: "var(--theme-text)", backgroundColor: "var(--theme-card-bg)" }}
        />
        
        {/* 参数设置折叠面板 */}
        <Collapse
          bordered={false}
          style={{ marginBottom: "10px" }}
          items={[
            {
              key: '1',
              label: <><SettingOutlined /> 参数设置</>,
              children: (
                <div style={{ padding: "10px 0" }}>
                  <Row gutter={[16, 16]}>
                    <Col span={12}>
                      <div style={{ marginBottom: "5px" }}>文本生成长度: {params.max_new_tokens}</div>
                      <Slider
                        min={100}
                        max={2000}
                        value={params.max_new_tokens}
                        onChange={(value) => handleParamChange('max_new_tokens', value)}
                      />
                      <InputNumber
                        min={100}
                        max={2000}
                        value={params.max_new_tokens}
                        onChange={(value) => handleParamChange('max_new_tokens', value)}
                        style={{ width: "100%", color: "var(--theme-text)", backgroundColor: "var(--theme-card-bg)" }}
                      />
                    </Col>
                    <Col span={12}>
                      <div style={{ marginBottom: "5px" }}>生成温度: {params.temperature.toFixed(2)}</div>
                      <Slider
                        min={0.1}
                        max={2}
                        step={0.1}
                        value={params.temperature}
                        onChange={(value) => handleParamChange('temperature', value)}
                      />
                      <InputNumber
                        min={0.1}
                        max={2}
                        step={0.1}
                        value={params.temperature}
                        onChange={(value) => handleParamChange('temperature', value)}
                        style={{ width: "100%", color: "var(--theme-text)", backgroundColor: "var(--theme-card-bg)" }}
                      />
                    </Col>
                    <Col span={12}>
                      <div style={{ marginBottom: "5px" }}>Top-K采样: {params.top_k}</div>
                      <Slider
                        min={1}
                        max={100}
                        value={params.top_k}
                        onChange={(value) => handleParamChange('top_k', value)}
                      />
                      <InputNumber
                        min={1}
                        max={100}
                        value={params.top_k}
                        onChange={(value) => handleParamChange('top_k', value)}
                        style={{ width: "100%", color: "var(--theme-text)", backgroundColor: "var(--theme-card-bg)" }}
                      />
                    </Col>
                    <Col span={12}>
                      <div style={{ marginBottom: "5px" }}>Top-P采样: {params.top_p.toFixed(2)}</div>
                      <Slider
                        min={0.1}
                        max={1}
                        step={0.05}
                        value={params.top_p}
                        onChange={(value) => handleParamChange('top_p', value)}
                      />
                      <InputNumber
                        min={0.1}
                        max={1}
                        step={0.05}
                        value={params.top_p}
                        onChange={(value) => handleParamChange('top_p', value)}
                        style={{ width: "100%", color: "var(--theme-text)", backgroundColor: "var(--theme-card-bg)" }}
                      />
                    </Col>
                    <Col span={12}>
                      <div style={{ marginBottom: "5px" }}>重复惩罚: {params.repetition_penalty.toFixed(2)}</div>
                      <Slider
                        min={1}
                        max={2}
                        step={0.1}
                        value={params.repetition_penalty}
                        onChange={(value) => handleParamChange('repetition_penalty', value)}
                      />
                      <InputNumber
                        min={1}
                        max={2}
                        step={0.1}
                        value={params.repetition_penalty}
                        onChange={(value) => handleParamChange('repetition_penalty', value)}
                        style={{ width: "100%", color: "var(--theme-text)", backgroundColor: "var(--theme-card-bg)" }}
                      />
                    </Col>
                    <Col span={12}>
                      <div style={{ marginBottom: "5px" }}>防止重复N-gram: {params.no_repeat_ngram_size}</div>
                      <Slider
                        min={0}
                        max={5}
                        value={params.no_repeat_ngram_size}
                        onChange={(value) => handleParamChange('no_repeat_ngram_size', value)}
                      />
                      <InputNumber
                        min={0}
                        max={5}
                        value={params.no_repeat_ngram_size}
                        onChange={(value) => handleParamChange('no_repeat_ngram_size', value)}
                        style={{ width: "100%", color: "var(--theme-text)", backgroundColor: "var(--theme-card-bg)" }}
                      />
                    </Col>
                    <Col span={12}>
                      <div style={{ marginBottom: "5px" }}>动态温度: {params.dynamic_temperature ? '开启' : '关闭'}</div>
                      <InputNumber
                        min={0}
                        max={1}
                        value={params.dynamic_temperature ? 1 : 0}
                        onChange={(value) => handleParamChange('dynamic_temperature', value === 1)}
                        style={{ width: "100%", color: "var(--theme-text)", backgroundColor: "var(--theme-card-bg)" }}
                      />
                    </Col>
                    <Col span={12}>
                      <div style={{ marginBottom: "5px" }}>动态top-p: {params.dynamic_top_p ? '开启' : '关闭'}</div>
                      <InputNumber
                        min={0}
                        max={1}
                        value={params.dynamic_top_p ? 1 : 0}
                        onChange={(value) => handleParamChange('dynamic_top_p', value === 1)}
                        style={{ width: "100%", color: "var(--theme-text)", backgroundColor: "var(--theme-card-bg)" }}
                      />
                    </Col>
                    <Col span={12}>
                      <div style={{ marginBottom: "5px" }}>存在惩罚: {params.presence_penalty.toFixed(2)}</div>
                      <Slider
                        min={-2}
                        max={2}
                        step={0.1}
                        value={params.presence_penalty}
                        onChange={(value) => handleParamChange('presence_penalty', value)}
                      />
                      <InputNumber
                        min={-2}
                        max={2}
                        step={0.1}
                        value={params.presence_penalty}
                        onChange={(value) => handleParamChange('presence_penalty', value)}
                        style={{ width: "100%", color: "var(--theme-text)", backgroundColor: "var(--theme-card-bg)" }}
                      />
                    </Col>
                    <Col span={12}>
                      <div style={{ marginBottom: "5px" }}>频率惩罚: {params.frequency_penalty.toFixed(2)}</div>
                      <Slider
                        min={-2}
                        max={2}
                        step={0.1}
                        value={params.frequency_penalty}
                        onChange={(value) => handleParamChange('frequency_penalty', value)}
                      />
                      <InputNumber
                        min={-2}
                        max={2}
                        step={0.1}
                        value={params.frequency_penalty}
                        onChange={(value) => handleParamChange('frequency_penalty', value)}
                        style={{ width: "100%", color: "var(--theme-text)", backgroundColor: "var(--theme-card-bg)" }}
                      />
                    </Col>
                  </Row>
                </div>
              ),
            },
          ]}
        />
        
        <Space direction="horizontal" style={{ width: "100%", justifyContent: "flex-end" }}>
          <Button icon={<ClearOutlined />} onClick={handleClear}>
            清空
          </Button>
          <Button
            type="primary"
            icon={<SendOutlined />}
            onClick={handleGenerate}
            loading={loading}
          >
            生成文本
          </Button>
        </Space>
      </Card>

      <Card
        className="card-glow"
        title="生成结果"
        extra={
          <Button
            icon={<SaveOutlined />}
            onClick={handleSave}
            disabled={!generatedText?.trim()} // 添加可选链
          >
            保存文本
          </Button>
        }
      >
        {loading ? (
          <div style={{ textAlign: "center", padding: "50px" }}>
            <Spin size="large" tip="正在生成剑来风格文本..." />
          </div>
        ) : generatedText ? (
          <div
            style={{ whiteSpace: "pre-wrap", lineHeight: 1.8, color: "var(--theme-text)" }}
            dangerouslySetInnerHTML={{ __html: (generatedText || "").replace(/\n/g, '<br />') }} // 添加空值检查
          />
        ) : (
          <div style={{ textAlign: "center", padding: "50px", color: "var(--theme-text)", opacity: 0.7 }}>
            点击生成按钮开始生成文本
          </div>
        )}
      </Card>

      <Divider />

      {/* 技术架构 */}
      <Card 
        title="技术架构" 
        style={{ marginBottom: '32px', background: 'var(--theme-card-bg)', border: '1px solid var(--theme-card-border)' }}
        titleStyle={{ color: 'var(--theme-text)' }}
      >
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} lg={8}>
            <div style={{ 
              padding: '16px', 
              background: 'rgba(255, 255, 255, 0.05)', 
              borderRadius: '8px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '24px', marginBottom: '8px', color: 'var(--theme-primary)' }}>🧠</div>
              <Title level={5} style={{ color: 'var(--theme-text)' }}>深度学习模型</Title>
              <Paragraph style={{ margin: 0, fontSize: '14px', color: 'var(--theme-text-secondary)' }}>
                基于先进的语言模型架构，经过大规模文本训练
              </Paragraph>
            </div>
          </Col>
          <Col xs={24} sm={12} lg={8}>
            <div style={{ 
              padding: '16px', 
              background: 'rgba(255, 255, 255, 0.05)', 
              borderRadius: '8px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '24px', marginBottom: '8px', color: 'var(--theme-primary)' }}>📝</div>
              <Title level={5} style={{ color: 'var(--theme-text)' }}>风格迁移技术</Title>
              <Paragraph style={{ margin: 0, fontSize: '14px', color: 'var(--theme-text-secondary)' }}>
                专注于《剑来》风格的文本迁移与生成
              </Paragraph>
            </div>
          </Col>
          <Col xs={24} sm={12} lg={8}>
            <div style={{ 
              padding: '16px', 
              background: 'rgba(255, 255, 255, 0.05)', 
              borderRadius: '8px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '24px', marginBottom: '8px', color: 'var(--theme-primary)' }}>⚡</div>
              <Title level={5} style={{ color: 'var(--theme-text)' }}>实时推理引擎</Title>
              <Paragraph style={{ margin: 0, fontSize: '14px', color: 'var(--theme-text-secondary)' }}>
                高性能推理引擎，确保快速响应和稳定服务
              </Paragraph>
            </div>
          </Col>
        </Row>
      </Card>

      {/* 使用说明 */}
      <Card
        className="card-glow"
        style={{ marginBottom: '32px' }}
      >
        <Title level={4} style={{ marginBottom: "10px", color: "var(--theme-text)" }}>使用说明</Title>
        <Paragraph style={{ color: "var(--theme-text)" }}>
          1. 在输入框中输入文本开头，例如：陈平安站在山巅，眯眼望向远方<br />
          2. 点击设置按钮，可调整文本生成长度、生成温度等参数，不会调的使用默认参数即可<br />
          3. 点击生成按钮，系统会生成剑来风格的文本<br />
          4. 可以保存生成的文本到本地文件<br />
          5. 提示词输入越详细，生成的文本越符合预期
        </Paragraph>
      </Card>

      {/* 项目链接 */}
      <div style={{ textAlign: 'center' }}>
        <Space wrap>
          <Button 
            type="primary" 
            icon={<GithubOutlined />}
            onClick={() => window.open('https://github.com', '_blank')}
          >
            GitHub仓库
          </Button>
          <Button 
            icon={<InfoCircleOutlined />}
            onClick={() => window.location.href = '/post/1766389977715'}
          >
            项目文档
          </Button>
          <Button 
            onClick={() => window.history.back()}
          >
            返回项目列表
          </Button>
        </Space>
      </div>
    </Content>
  </Layout>
  );
}